'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import nl from '../../locales/nl.json';
import en from '../../locales/en.json';
import fr from '../../locales/fr.json';
import tr from '../../locales/tr.json';
import moment from 'moment';

// Import moment.js locales
import 'moment/locale/nl';
import 'moment/locale/fr';
import 'moment/locale/tr';

const locales = { nl, en, fr, tr };
export type Locale = keyof typeof locales;

// Map app locales to moment.js locales
const momentLocaleMap: Record<Locale, string> = {
  nl: 'nl',
  en: 'en',
  fr: 'fr',
  tr: 'tr',
};

interface LanguageContextProps {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextProps | null>(null);

export const LanguageProvider = ({ children, initialLocale }: { children: ReactNode; initialLocale: Locale }) => {
  // Use server-provided locale as initial state to prevent hydration mismatch
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [isHydrated, setIsHydrated] = useState(false);

  // After hydration, sync locale to localStorage and cookie
  useEffect(() => {
    setIsHydrated(true);
    if (typeof window !== 'undefined') {
      // Check if localStorage has a preference
      const saved = localStorage.getItem('betcenter-locale') as Locale;
      
      if (!saved) {
        // No localStorage preference, set it to match the server-detected locale
        localStorage.setItem('betcenter-locale', locale);
      } else if (saved !== "fr") {
        // RTBF variant is French-only.
        setLocaleState("fr");
        localStorage.setItem('betcenter-locale', "fr");
      }
      
      // Always sync cookie for server-side detection
      document.cookie = `betcenter-locale=${locale}; path=/; max-age=31536000`; // 1 year
    }
  }, []); // Only run once after mount

  // Sync locale to cookie and moment.js whenever it changes (after hydration)
  useEffect(() => {
    if (typeof window !== 'undefined' && isHydrated) {
      document.cookie = `betcenter-locale=${locale}; path=/; max-age=31536000`; // 1 year
      // Set moment.js locale based on app locale
      const momentLocale = momentLocaleMap[locale] || 'en';
      moment.locale(momentLocale);
    }
  }, [locale, isHydrated]);

  // Set initial moment.js locale on mount
  useEffect(() => {
    const momentLocale = momentLocaleMap[locale] || 'en';
    moment.locale(momentLocale);
  }, []); // Only run once on mount

  const setLocale = (newLocale: Locale) => {
    // RTBF variant is French-only.
    const forcedLocale: Locale = "fr";
    setLocaleState(forcedLocale);
    localStorage.setItem('betcenter-locale', forcedLocale);
    // Sync to cookie for server-side detection
    document.cookie = `betcenter-locale=${forcedLocale}; path=/; max-age=31536000`; // 1 year
  };

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = locales[locale];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }
    if (typeof value === 'string') return value;
    return key;
  };

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};


